import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BRAND from '@/brand';
import Wordmark from '@/components/Wordmark';
import {
  Loader2, Check, ArrowRight, Landmark, Mail, Receipt, Upload, FileText, UserPlus, Forward, Cloud, Search,
  Smartphone, ChevronDown,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Designets farver. De står her frem for i temaet, fordi siden med vilje er
// uafhængig af lyst/mørkt tema: den er DigitalBox' ansigt udadtil og skal se
// ens ud for alle, også før nogen har valgt noget.
const F = {
  navy: '#191c2d', blaek: '#161a2b', sek: '#2b3040', muted: '#5b6270',
  teal: '#1fc6ad', tealHover: '#19b39c', link: '#12a892',
  tealLys: '#c9efe7', tealLysere: '#d7f4ee',
  kant: '#cfd5da', kantHover: '#b5bdc4', skillelinje: '#d6dbdf',
  sidebg: '#e8eeeb', fejl: '#c0392b',
};

const gyldigEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

// Stilarket ligger i komponenten frem for som inline-styles. Inline kan ikke
// udtrykke :hover, :focus eller :active — og netop dem beskriver designet
// praecist for hver knap. Klassenavnene er praefikset, saa de ikke kan kollidere
// med resten af appen.
const STIL = `
.li-knap {
  height: 44px; width: 100%; border: 0; border-radius: 6px;
  background: ${F.teal}; color: #10131f;
  font-size: 16px; font-weight: 700; letter-spacing: -0.01em;
  cursor: pointer; position: relative;
  display: flex; align-items: center; justify-content: center;
  transition: background-color .12s ease;
}
.li-knap:hover:not(:disabled)  { background: ${F.tealHover}; }
.li-knap:active:not(:disabled) { background: #15a08b; }
.li-knap:disabled              { cursor: wait; opacity: .75; }
.li-knap .li-pil {
  position: absolute; right: 16px; display: inline-flex; align-items: center;
}

.li-sso {
  height: 44px; display: flex; align-items: center; gap: 16px; padding: 0 16px;
  border: 1px solid ${F.kant}; border-radius: 6px; background: #fff;
  font-size: 15px; font-weight: 500; color: ${F.blaek}; text-decoration: none;
  transition: background-color .12s ease, border-color .12s ease;
}
.li-sso:hover { background: #f5f7f8; border-color: ${F.kantHover}; }

.li-felt {
  height: 42px; width: 100%; border-radius: 6px; padding: 0 12px;
  font-size: 15px; color: ${F.blaek}; background: #fff;
  border: 1px solid ${F.kant}; outline: none;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.li-felt::placeholder { color: #9aa3ad; }
.li-felt:focus        { border-color: ${F.teal}; box-shadow: 0 0 0 3px rgba(31,198,173,.2); }
.li-felt.li-fejl      { border-color: ${F.fejl}; }
.li-felt.li-fejl:focus{ box-shadow: 0 0 0 3px rgba(192,57,43,.15); }

.li-link  { color: ${F.link}; font-weight: 600; text-decoration: none; }
.li-link:hover { color: #0c8574; text-decoration: underline; }
.li-tekstknap {
  background: none; border: 0; padding: 0; font-size: 13px; font-weight: 600;
  color: ${F.link}; cursor: pointer;
}
.li-tekstknap:hover:not(:disabled) { color: #0c8574; text-decoration: underline; }
.li-tekstknap:disabled { color: ${F.muted}; cursor: default; }
.li-fod a { color: ${F.muted}; text-decoration: none; }
.li-fod a:hover { text-decoration: underline; }

/* Den buede understregning under accentordet. En almindelig border ville
   vaere en lige streg; designet beder om en bue, og det kraever en form. */
.li-accent { color: ${F.teal}; position: relative; white-space: nowrap; }
.li-accent svg {
  position: absolute; left: 0; bottom: -10px; width: 100%; height: 10px;
  overflow: visible;
}

/* Paa smalle skaerme lagres panelerne, og den store bue ville skaere hjoernet
   af illustrationen. Radius bliver ens hele vejen rundt i stedet. */
@media (max-width: 900px) {
  .li-brand { border-radius: 22px 22px 0 0 !important; padding: 32px 28px 36px !important; }
  .li-brand h1 { font-size: 34px !important; }
  .li-brand .li-indryk { padding-left: 0 !important; }
  .li-form { padding: 20px 24px 20px !important; }
}
`;

// ── Illustrationen: posten sorteres ind i én indbakke og videre ─────────────
// Figma-designets "Hero / Sorting line" (docs/design/digitalbox/landing-page.png):
// fire kilder foroven, indbakken i midten og tre udfald forneden. Bygget i
// kode frem for som billede, saa den skalerer med panelet og er skarp overalt.
const KILDER = [
  { ikon: Landmark, titel: 'Digital Post', tekst: 'Det offentlige', aktiv: true },
  { ikon: Mail, titel: 'e-Boks', tekst: 'Beskeder' },
  { ikon: Receipt, titel: 'Bilagsadresse', tekst: 'Fra leverandører' },
  { ikon: Upload, titel: 'Upload', tekst: 'Jeres egne filer' },
];
const RAEKKER = [
  { ikon: Landmark, afs: 'Skattestyrelsen', emne: 'Årsopgørelse for moms', tid: '09:14', maerke: 'ny', valgt: true },
  { ikon: Mail, afs: 'Lønservice via e-Boks', emne: 'Lønsedler for september', tid: 'I går' },
  { ikon: Receipt, afs: 'Kontorland A/S', emne: 'Faktura 20418 · 1 bilag', tid: '25. sep', maerke: 'tildelt' },
  { ikon: FileText, afs: 'Lejekontrakt_2026.pdf', emne: 'Uploadet af Jonas', tid: '24. sep', maerke: 'faerdig' },
];
const UDFALD = [
  { ikon: UserPlus, titel: 'Tildelt', tekst: 'Maja, bogholder' },
  { ikon: Forward, titel: 'Videresendt', tekst: 'Til revisor', aktiv: true },
  { ikon: Cloud, titel: 'Arkiveret', tekst: 'OneDrive › Bilag' },
];

function Flise({ ikon: Ikon, titel, tekst, aktiv }) {
  return (
    <div style={{ background: aktiv ? F.teal : 'rgba(255,255,255,.09)', color: aktiv ? '#0f2a26' : '#fff' }}
      className="rounded-xl px-2 sm:px-3 py-2.5 min-w-0">
      <Ikon size={15} strokeWidth={2} />
      <p className="mt-1.5 text-[11px] sm:text-[13px] font-semibold leading-4 truncate">{titel}</p>
      <p className="text-[11px] leading-4 truncate hidden sm:block" style={{ opacity: aktiv ? .8 : .6 }}>{tekst}</p>
    </div>
  );
}

// Buerne mellem fliserne og indbakken. Tegnet i en fast viewBox og strakt ud
// i bredden; non-scaling-stroke holder stregen lige tyk, uanset bredden.
function Buer({ fra, til, aktivIndex, hoejde = 34 }) {
  return (
    <svg viewBox={`0 0 100 ${hoejde}`} preserveAspectRatio="none" className="block w-full" style={{ height: hoejde }}>
      {fra.map((x, i) => (
        <path key={i} d={`M${x},0 C${x},${hoejde / 2} ${til[i]},${hoejde / 2} ${til[i]},${hoejde}`}
          fill="none" stroke={i === aktivIndex ? F.teal : '#fff'} strokeWidth="3"
          vectorEffect="non-scaling-stroke" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function Maerke({ type }) {
  if (type === 'ny') return <span className="px-2 py-0.5 rounded text-[10px] font-semibold text-white" style={{ background: F.teal }}>Ny</span>;
  if (type === 'tildelt') return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#e6ebff', color: F.blaek }}>
      <span className="w-1 h-1 rounded-full" style={{ background: '#3a5bff' }} />Tildelt Maja
    </span>
  );
  if (type === 'faerdig') return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: '#dce7e2', color: F.blaek }}>
      <span className="w-1 h-1 rounded-full" style={{ background: F.teal }} />Færdigbehandlet
    </span>
  );
  return null;
}

function Illustration() {
  return (
    <div aria-hidden="true" className="select-none" style={{ maxWidth: 560 }}>
      <div className="grid grid-cols-4 gap-2">{KILDER.map(k => <Flise key={k.titel} {...k} />)}</div>

      <div className="relative">
        <Buer fra={[12.5, 37.5, 62.5, 87.5]} til={[38, 46, 54, 62]} aktivIndex={0} />
        <span className="absolute flex items-center justify-center w-6 h-5 rounded bg-white"
          style={{ left: '22%', top: 8, transform: 'rotate(-14deg)', boxShadow: '0 2px 6px rgba(0,0,0,.3)' }}>
          <Mail size={12} color={F.blaek} />
        </span>
      </div>

      <div className="rounded-2xl bg-white overflow-hidden" style={{ boxShadow: '0 18px 40px rgba(0,0,0,.35)', color: F.blaek }}>
        <div className="flex items-center gap-2 px-4 py-3">
          <span className="text-[15px] font-bold">Indbakke</span>
          <span className="hidden sm:inline text-[10px] font-semibold px-2 py-0.5 rounded border truncate" style={{ borderColor: F.kant }}>Nordlys ApS · CVR 12 34 56 78</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md" style={{ background: '#f2f4f5', color: F.muted }}>
            <Search size={11} /> Søg
          </span>
        </div>
        {RAEKKER.map(r => (
          <div key={r.afs} className="flex items-center gap-2.5 px-4 py-2 border-t" style={{ borderColor: '#e6e9e7', background: r.valgt ? '#dce7e2' : '#fff' }}>
            <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: r.maerke === 'ny' ? F.teal : '#f2f4f5', color: r.maerke === 'ny' ? '#fff' : F.blaek }}>
              <r.ikon size={14} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[12px] font-medium leading-4 truncate">{r.afs}</span>
              <span className="block text-[11px] leading-4 truncate" style={{ color: F.muted }}>{r.emne}</span>
            </span>
            <span className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-[10px] font-semibold" style={{ color: F.muted }}>{r.tid}</span>
              <Maerke type={r.maerke} />
            </span>
          </div>
        ))}
      </div>

      <Buer fra={[42, 50, 58]} til={[16.7, 50, 83.3]} aktivIndex={1} />
      <div className="grid grid-cols-3 gap-2">{UDFALD.map(u => <Flise key={u.titel} {...u} />)}</div>
    </div>
  );
}

// ── Log ind med SMS ──────────────────────────────────────────────────────────
// Brugeren sender en faerdigskrevet SMS til vores 2-vejs-nummer; vi sender ingen
// kode. Paa mobil aabner knappen Beskeder direkte — sessionen hentes paa
// forhaand, fordi browsere afviser et sms:-link, der aabnes efter et netvaerkskald.
// Paa computeren folder knappen en QR-kode ud, som telefonen scanner.
const erMobil = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
const smsLink = (til, tekst) => {
  // iOS og macOS vil have "&body=", Android "?&body=" — begge tager imod deres egen
  const apple = /Macintosh|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return `sms:${til}${apple ? '&' : '?&'}body=${encodeURIComponent(tekst)}`;
};
const SMS_AFVIST = {
  unknown_number: 'Nummeret hører ikke til en konto med bekræftet mobilnummer. Log ind med e-mail i stedet.',
  shared_number: 'Flere konti bruger det nummer, så vi kan ikke se, hvem du er. Log ind med e-mail i stedet.',
};

function SmsLogin({ onLinkToken }) {
  const mobil = erMobil();
  const [aaben, setAaben] = useState(false);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('idle');   // idle|loading|pending|vaelg|verified|denied|expired|fejl
  const [grund, setGrund] = useState('');
  const [konti, setKonti] = useState([]);         // ved flere konti paa samme nummer
  const [vaelger, setVaelger] = useState(null);

  const hent = async () => {
    setStatus('loading'); setGrund('');
    try {
      const r = await axios.post(`${API}/auth/sms-login/session`);
      setSession(r.data); setStatus('pending');
    } catch { setStatus('fejl'); }
  };

  useEffect(() => { if (mobil) hent(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!mobil && aaben && !session) hent(); }, [aaben]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spoerg hvert andet sekund, mens der ventes paa SMS'en
  useEffect(() => {
    if (!session || status !== 'pending' || !aaben) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/sms-login/session/${session.id}`, { params: { noegle: session.noegle } });
        if (data.status === 'verified') { clearInterval(iv); setStatus('verified'); onLinkToken(data.link_token); }
        else if (data.status === 'vaelg') { clearInterval(iv); setKonti(data.konti || []); setStatus('vaelg'); }
        else if (data.status === 'denied') { setStatus('denied'); setGrund(data.reason); }
        else if (data.status === 'expired') setStatus('expired');
      } catch { /* naeste forsoeg */ }
    }, 2000);
    return () => clearInterval(iv);
  }, [session, status, aaben, onLinkToken]);

  const vaelgKonto = async (id) => {
    setVaelger(id);
    try {
      const { data } = await axios.post(`${API}/auth/sms-login/session/${session.id}/vaelg`, { noegle: session.noegle, user_id: id });
      setStatus('verified'); onLinkToken(data.link_token);
    } catch { setStatus('fejl'); setVaelger(null); }
  };

  const link = session ? smsLink(session.sms_to, session.sms_body) : '#';
  // Samme 24 px-ramme som Google- og Microsoft-logoet, saa teksten flugter
  const ikon = (
    <span style={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Smartphone size={21} color={F.blaek} />
    </span>
  );

  const statuslinje = (
    <div style={{ fontSize: 13, color: F.sek, display: 'flex', alignItems: 'center', gap: 8 }}>
      {status === 'pending' && <><Loader2 size={15} className="animate-spin" /> Venter på din SMS…</>}
      {status === 'loading' && <><Loader2 size={15} className="animate-spin" /> Gør klar…</>}
      {status === 'verified' && <><Check size={15} color={F.link} /> Logget ind — et øjeblik…</>}
      {status === 'vaelg' && (
        // Nummeret staar bekraeftet paa flere konti: brugeren vaelger selv
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
          <span>Dit nummer bruges af flere konti. Hvilken vil du logge ind på?</span>
          {konti.map(k => (
            <button key={k.id} type="button" className="li-sso" disabled={!!vaelger} onClick={() => vaelgKonto(k.id)}
              style={{ cursor: 'pointer', height: 'auto', padding: '8px 12px', textAlign: 'left', width: '100%' }}>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <strong style={{ fontSize: 14, color: F.blaek }}>{k.navn || k.email}</strong>
                {k.navn && <span style={{ fontSize: 12, color: F.muted }}>{k.email}</span>}
              </span>
              {vaelger === k.id ? <Loader2 size={15} className="animate-spin" style={{ marginLeft: 'auto' }} /> : <ArrowRight size={15} style={{ marginLeft: 'auto' }} color={F.muted} />}
            </button>
          ))}
        </div>
      )}
      {status === 'denied' && <span style={{ color: F.fejl }}>{SMS_AFVIST[grund] || SMS_AFVIST.unknown_number}</span>}
      {(status === 'expired' || status === 'fejl') && (
        <span>{status === 'expired' ? 'Tiden løb ud.' : 'Det mislykkedes.'}{' '}
          <button type="button" className="li-tekstknap" onClick={hent}>Prøv igen</button></span>
      )}
    </div>
  );

  if (mobil) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <a href={link} className="li-sso"
          onClick={(e) => {
            if (status !== 'pending') { e.preventDefault(); hent(); return; }
            setAaben(true);
          }}>
          {ikon} Log ind med SMS
        </a>
        {aaben && statuslinje}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button type="button" className="li-sso" aria-expanded={aaben} onClick={() => setAaben(v => !v)}
        style={{ cursor: 'pointer', width: '100%' }}>
        {ikon} Log ind med SMS
        <ChevronDown size={18} color={F.muted} style={{ marginLeft: 'auto', transition: 'transform .15s', transform: aaben ? 'rotate(180deg)' : 'none' }} />
      </button>
      {aaben && (
        <div style={{ border: `1px solid ${F.kant}`, borderRadius: 6, padding: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          {/* QR-koden er kun til at sende SMS'en — naar den er modtaget, er den vaek */}
          {!['vaelg', 'verified'].includes(status) && (
            <div style={{ background: '#fff', padding: 6, borderRadius: 6, border: `1px solid ${F.kant}`, flexShrink: 0, lineHeight: 0 }}>
              {session ? <QRCodeSVG value={link} size={120} /> : <div style={{ width: 120, height: 120 }} />}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, flex: 1 }}>
            {!['vaelg', 'verified'].includes(status) && (
              <p style={{ fontSize: 14, color: F.blaek, margin: 0, lineHeight: 1.4 }}>
                Scan koden med din telefon, og tryk <strong>Send</strong> på den færdigskrevne SMS.
              </p>
            )}
            {statuslinje}
            {session && status === 'pending' && (
              <a href={link} className="li-link" style={{ fontSize: 13 }}>Eller send fra computeren</a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SSO. Knapperne vises kun, når serveren siger, at metoden findes ─────────
const SSO = {
  google: {
    navn: 'Google',
    ikon: (
      <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.9-2.2 5.3-4.7 7l7.6 5.9c4.4-4.1 6.8-10.1 6.8-17.4z" />
        <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C1 16.3 0 20 0 24s1 7.7 2.6 10.8l7.8-6.1z" />
        <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.4 0-11.7-3.7-13.6-8.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
      </svg>
    ),
  },
  microsoft: {
    navn: 'Microsoft',
    ikon: (
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#F25022" d="M1 1h10.2v10.2H1z" /><path fill="#7FBA00" d="M12.8 1H23v10.2H12.8z" />
        <path fill="#00A4EF" d="M1 12.8h10.2V23H1z" /><path fill="#FFB900" d="M12.8 12.8H23V23H12.8z" />
      </svg>
    ),
  },
};

function SsoKnap({ id, arbejder }) {
  const m = SSO[id];
  return (
    <a href={`${API}/auth/oauth/${id}/start`} className="li-sso"
       style={{ pointerEvents: arbejder ? 'none' : 'auto', opacity: arbejder ? .6 : 1 }}>
      {m.ikon}<span>Fortsæt med {m.navn}</span>
    </a>
  );
}

export default function LogIndPage({ tilstand = 'login' }) {
  const [soegeparametre] = useSearchParams();
  const navigate = useNavigate();
  const { loginMedLink } = useAuth();

  const [email, setEmail] = useState('');
  const [fejl, setFejl] = useState('');
  const [sendt, setSendt] = useState(false);
  const [gensendt, setGensendt] = useState(false);
  const [arbejder, setArbejder] = useState(false);
  const [metoder, setMetoder] = useState([]);
  const [linkFejl, setLinkFejl] = useState('');
  const [indloeser, setIndloeser] = useState(false);

  const opretter = tilstand === 'opret';

  // Hvilke login-metoder findes? Knapper til noget, der ikke er sat op, er
  // værre end ingen knapper: de ser ud som om de virker.
  useEffect(() => {
    axios.get(`${API}/auth/metoder`)
      .then(r => setMetoder(r.data?.sso || []))
      .catch(() => setMetoder([]));
  }, []);

  // OAuth-flowet sender fejl tilbage som ?fejl=… frem for en rå JSON-side.
  useEffect(() => {
    const f = soegeparametre.get('fejl');
    if (f) setLinkFejl(f);
  }, [soegeparametre]);

  // Kommer brugeren fra et e-mail-link eller fra Google/Microsoft, indløses
  // engangstokenet med det samme. Begge veje ender her — sessionen kommer
  // aldrig i URL'en, kun et token der dør ved første brug.
  useEffect(() => {
    const t = soegeparametre.get('token');
    if (!t) return;
    setIndloeser(true);
    // Ny bruger fra Google/Microsoft (ny=1): foerst hen og tilfoeje et selskab
    const maal = soegeparametre.get('ny') === '1' ? '/selskaber' : '/indbakke';
    loginMedLink(t)
      .then(() => navigate(maal, { replace: true }))
      .catch(e => {
        setLinkFejl(e?.response?.data?.detail || 'Linket kunne ikke bruges');
        setIndloeser(false);
      });
  }, [soegeparametre, loginMedLink, navigate]);

  // SMS-login ender i samme engangslink som e-mail og Google/Microsoft
  const smsIndloes = React.useCallback((linkToken) => {
    setIndloeser(true);
    loginMedLink(linkToken)
      .then(() => navigate('/indbakke', { replace: true }))
      .catch(e => { setLinkFejl(e?.response?.data?.detail || 'Login mislykkedes'); setIndloeser(false); });
  }, [loginMedLink, navigate]);

  const send = async (e) => {
    e?.preventDefault();
    if (!gyldigEmail(email)) { setFejl('Indtast en gyldig e-mailadresse.'); return; }
    setFejl(''); setArbejder(true);
    try {
      await axios.post(`${API}/auth/magisk-link`, { email: email.trim(), opret: opretter });
      setSendt(true);
    } catch (e2) {
      setFejl(e2?.response?.status === 429
        ? 'For mange forsøg. Prøv igen om lidt.'
        : 'Kunne ikke sende linket. Prøv igen.');
    } finally { setArbejder(false); }
  };

  const gensend = async () => {
    setArbejder(true);
    try {
      await axios.post(`${API}/auth/magisk-link`, { email: email.trim(), opret: opretter });
      setGensendt(true);
    } catch { /* stille: linket er allerede sendt én gang */ }
    finally { setArbejder(false); }
  };

  if (indloeser) {
    return (
      <div style={{ minHeight: '100vh', background: F.sidebg, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', gap: 10, color: F.sek }}>
        <Loader2 className="animate-spin" size={18} /> Logger dig ind …
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: F.sidebg, padding: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{STIL}</style>
      <div style={{
        width: '100%', maxWidth: 1360, minHeight: 560, background: '#fff',
        borderRadius: 22, display: 'flex', flexWrap: 'wrap', overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(20,30,40,.05), 0 20px 50px -20px rgba(20,40,40,.18)',
      }}>
        {/* Brand-panel */}
        <div className="li-brand" style={{
          flex: '1 1 560px', minWidth: 0, background: F.navy, color: '#fff',
          borderRadius: '22px 90px 90px 22px', padding: '40px 48px 48px',
          display: 'flex', flexDirection: 'column', gap: 28,
        }}>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>
            <Wordmark />
          </div>
          <div className="li-indryk" style={{ paddingLeft: 56 }}>
            <h1 style={{ fontSize: 44, lineHeight: 1.05, fontWeight: 800,
                         letterSpacing: '-0.03em', margin: 0 }}>
              {opretter ? <>Dit overblik<br /><Accent>starter her.</Accent></>
                        : <>Mindre administration.<br />Mere <Accent>overblik.</Accent></>}
            </h1>
            <p style={{ fontSize: 18, color: '#dfe3ea', margin: '14px 0 0' }}>
              Din digitale post og dine bilag. Samlet ét sted.
            </p>
          </div>
          <div className="li-indryk" style={{ paddingLeft: 56, marginTop: 'auto', minWidth: 0 }}><Illustration /></div>
        </div>

        {/* Formular-panel */}
        <div className="li-form" style={{ flex: '1 1 420px', padding: '24px 36px 22px',
                      display: 'flex', flexDirection: 'column' }}>
          <p style={{ fontSize: 13, textAlign: 'right', color: F.sek, margin: 0 }}>
            {opretter ? <>Har du en konto? <Link to="/log-ind" className="li-link">Log ind</Link></>
                      : <>Ny hos {BRAND.name}? <Link to="/opret-konto" className="li-link">Opret konto</Link></>}
          </p>

          <div style={{ maxWidth: 344, width: '100%', margin: 'auto', display: 'flex',
                        flexDirection: 'column', gap: 18, padding: '28px 0' }}>
            {sendt ? (
              <>
                <span style={{
                  width: 52, height: 52, borderRadius: 999, background: F.tealLysere,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}><Check size={24} color="#0c8574" strokeWidth={2.5} /></span>
                <div>
                  <h2 style={h2Stil}>Tjek din indbakke</h2>
                  <p style={{ fontSize: 15, color: F.sek, margin: '8px 0 0' }}>
                    Vi har sendt et login-link til <strong>{email}</strong>.
                    Linket udløber om 15 minutter.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 18, fontSize: 13 }}>
                  <button onClick={gensend} disabled={arbejder || gensendt} className="li-tekstknap">
                    {gensendt ? 'Sendt igen ✓' : 'Send link igen'}
                  </button>
                  <button onClick={() => { setSendt(false); setGensendt(false); }} className="li-tekstknap">
                    Brug en anden e-mail
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <h2 style={h2Stil}>{opretter ? 'Opret din konto' : 'Velkommen tilbage'}</h2>
                  <p style={{ fontSize: 15, color: F.sek, margin: '8px 0 0' }}>
                    {opretter ? `Kom i gang med ${BRAND.name}.`
                              : 'Log ind og få overblik over din hverdag.'}
                  </p>
                </div>

                {linkFejl && (
                  <p style={{ fontSize: 13, color: F.fejl, margin: 0 }}>
                    {linkFejl}. Bed om et nyt herunder.
                  </p>
                )}

                {(metoder.length > 0 || !opretter) && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {metoder.map(id => SSO[id] && <SsoKnap key={id} id={id} arbejder={arbejder} />)}
                      {/* SMS kraever en konto med bekraeftet nummer — derfor kun ved login */}
                      {!opretter && <SmsLogin onLinkToken={smsIndloes} />}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ flex: 1, height: 1, background: F.skillelinje }} />
                      <span style={{ fontSize: 12, color: F.muted }}>eller med e-mail</span>
                      <span style={{ flex: 1, height: 1, background: F.skillelinje }} />
                    </div>
                  </>
                )}

                <form onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label htmlFor="email" style={{ fontSize: 13, fontWeight: 500, color: F.blaek }}>
                    E-mailadresse
                  </label>
                  <input
                    id="email" type="email" value={email} autoComplete="email" autoFocus
                    onChange={(e) => { setEmail(e.target.value); if (fejl) setFejl(''); }}
                    placeholder="dig@virksomhed.dk"
                    className={`li-felt${fejl ? ' li-fejl' : ''}`}
                    aria-invalid={!!fejl} aria-describedby={fejl ? 'email-fejl' : undefined}
                  />
                  {fejl && <span id="email-fejl" style={{ fontSize: 12, color: F.fejl }}>{fejl}</span>}

                  <button type="submit" disabled={arbejder}
                          className="li-knap" style={{ marginTop: 10 }}>
                    <span>{opretter ? 'Opret konto' : 'Fortsæt'}</span>
                    <span className="li-pil">
                      {arbejder ? <Loader2 className="animate-spin" size={16} />
                                : <ArrowRight size={16} />}
                    </span>
                  </button>
                </form>

                <p style={{ fontSize: 13, color: F.sek, margin: 0 }}>
                  {opretter
                    ? <>Ved at fortsætte accepterer du vores <a href="/vilkaar" className="li-link">vilkår</a> og <a href="/privatliv" className="li-link">privatlivspolitik</a>.</>
                    : <>Brug for hjælp? <a href={`mailto:${BRAND.contact_email || ''}`} className="li-link">Kontakt os</a></>}
                </p>
              </>
            )}
          </div>

          <div className="li-fod" style={{ display: 'flex', justifyContent: 'flex-end',
                        fontSize: 12, color: F.muted }}>
            <span>
              <a href="/privatliv" style={{ color: F.muted }}>Privatlivspolitik</a>
              {' · '}
              <a href="/vilkaar" style={{ color: F.muted }}>Vilkår</a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const h2Stil = { fontSize: 29, fontWeight: 800, letterSpacing: '-0.03em',
                 color: F.blaek, margin: 0 };

// Accentordet med den buede understregning fra designet.
function Accent({ children }) {
  return (
    <span className="li-accent">
      {children}
      {/* Buen tegnes som en kurve. En border ville give en lige streg, og
          designet beder udtrykkeligt om en buet understregning. */}
      <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
        <path d="M1 7 Q 50 0.5 99 6" fill="none" stroke={F.teal}
              strokeWidth="4" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
    </span>
  );
}
